import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';

export interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

export interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// Strip HTML tags from a richtext value so it can be used as a plain
// string (e.g. as fallback alt text on a photo slider).
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Paginated amenities list
//
// The image panel has a fixed height. The list panel inherits that same row
// height. If the list of items would render taller than the visible area,
// we slice it into pages and show prev/next controls.
//
// Approach: a hidden "measurement" copy of the full list is rendered behind
// the visible list with the same width/layout but with no height constraint.
// We read the rendered position of every item row and greedily pack whole
// rows into pages, breaking before any row whose bottom edge would exceed the
// available height. This guarantees no item is ever partially clipped — each
// page only contains rows that fully fit. Because the grid is row-major with
// two columns, page boundaries always fall on a row start (an even item
// index), so a sliced page re-flows into exactly the same row pairing as the
// measurement pass.
// ============================================================================

interface PaginatedAmenitiesListProps {
  items: Amenity[];
}

// Bottom safety margin (px): the visible list's bottom padding plus a little
// slack for sub-pixel rounding, so the last row on a page never touches the
// clipped edge.
const PAGE_BOTTOM_MARGIN = 16;

function PaginatedAmenitiesList({ items }: PaginatedAmenitiesListProps) {
  // Each entry is the [startIndex, endIndex) item range for one page.
  const [pageRanges, setPageRanges] = useState<Array<[number, number]>>([[0, items.length]]);
  const [page, setPage] = useState(0);
  // Direction of the last user-initiated page change, used to slide the new
  // page in from the matching side. null until the first navigation so the
  // initial render doesn't animate.
  const [slideDir, setSlideDir] = useState<'next' | 'prev' | null>(null);

  const goToPage = useCallback((next: number, dir: 'next' | 'prev') => {
    setSlideDir(dir);
    setPage(next);
  }, []);

  const measureRef = useRef<HTMLUListElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (items.length === 0) {
      setPageRanges([[0, 0]]);
      return;
    }

    const measure = () => {
      const list = measureRef.current;
      const inner = innerRef.current;
      if (!list || !inner) return;
      const availH = inner.clientHeight - PAGE_BOTTOM_MARGIN;
      if (availH <= 0) return;

      // The grid is row-major with two columns, so each row holds up to two
      // items. Pair items by index and take each row's lowest bottom edge.
      const listTop = list.getBoundingClientRect().top;
      const lis = Array.from(list.children) as HTMLElement[];
      const rows: Array<{ start: number; top: number; bottom: number }> = [];
      for (let i = 0; i < lis.length; i += 2) {
        const a = lis[i].getBoundingClientRect();
        const b = lis[i + 1]?.getBoundingClientRect();
        rows.push({
          start: i,
          top: a.top - listTop,
          bottom: Math.max(a.bottom, b ? b.bottom : a.bottom) - listTop,
        });
      }
      if (rows.length === 0) {
        setPageRanges([[0, items.length]]);
        return;
      }

      // Greedily pack whole rows into pages. Break before any row whose
      // bottom (measured from the current page's first row) would overflow.
      const ranges: Array<[number, number]> = [];
      let pageStartRow = 0;
      for (let r = 1; r < rows.length; r++) {
        const pageTop = rows[pageStartRow].top;
        if (rows[r].bottom - pageTop > availH) {
          ranges.push([rows[pageStartRow].start, rows[r].start]);
          pageStartRow = r;
        }
      }
      ranges.push([rows[pageStartRow].start, items.length]);
      setPageRanges(ranges);
    };

    measure();
    setPage(0);

    if (typeof ResizeObserver !== 'undefined' && innerRef.current) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(innerRef.current);
      return () => ro.disconnect();
    }
  }, [items.length]);

  const totalPages = Math.max(1, pageRanges.length);
  const safePage = Math.min(page, totalPages - 1);
  const [rangeStart, rangeEnd] = pageRanges[safePage] || [0, items.length];
  const currentItems = items.slice(rangeStart, rangeEnd);

  return (
    <div className="amenities-list-area">
      <div ref={innerRef} className="amenities-list-area-inner">
        {/* Hidden measurement pass: full list rendered with no height cap so
            we can read its natural scrollHeight. */}
        <ul
          ref={measureRef}
          className="amenities-col-list amenities-col-list--measure"
          aria-hidden="true"
        >
          {items.map((item, i) => (
            <li key={`m-${i}`} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>

        {/* Visible (paginated) list. Re-keying by page remounts the list so
            the slide-in animation replays on every page change. */}
        <ul
          key={`page-${safePage}`}
          className={`amenities-col-list${slideDir ? ` amenities-col-list--in-${slideDir}` : ''}`}
        >
          {currentItems.map((item, i) => (
            <li key={`p${safePage}-${i}`} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>
      </div>

      {totalPages > 1 && (
        <div className="amenities-pagination">
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => goToPage(Math.max(0, safePage - 1), 'prev')}
            disabled={safePage === 0}
            aria-label="Previous amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="amenities-pagination-info">
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => goToPage(Math.min(totalPages - 1, safePage + 1), 'next')}
            disabled={safePage === totalPages - 1}
            aria-label="Next amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Column component
// ============================================================================

interface AmenityColumnProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
  imagePosition: 'left' | 'right';
}

export function AmenityColumn({ headingNode, headingText, items, photos, imagePosition }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const contentPanel = (
    <div className="amenities-row-content">
      {headingNode}
      <PaginatedAmenitiesList items={items} />
    </div>
  );

  const imagePanel = hasImage ? (
    <div className="amenities-row-image">
      <PhotoSlider photos={photos} heading={headingText} />
    </div>
  ) : null;

  return (
    <div
      ref={colRef}
      className={`reveal-fade-up amenities-row amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
    >
      {imagePosition === 'left' ? (
        <>
          {imagePanel}
          {contentPanel}
        </>
      ) : (
        <>
          {contentPanel}
          {imagePanel}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Shared styles — rendered once per amenities section.
// ============================================================================

export function AmenitiesStyles() {
  return (
    <style>{`
      /* Each row: side-by-side image + content with a fixed height so the
         image gallery never grows tall. The same layout is used on tablet
         and mobile — the row simply gets shorter on smaller viewports. */
      .amenities-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        background: white;
        overflow: hidden;
        height: 460px;
      }
      .amenities-row--no-image {
        grid-template-columns: 1fr;
        height: auto;
        min-height: 220px;
      }

      /* Content panel */
      .amenities-row-content {
        display: flex;
        flex-direction: column;
        padding: 0;
        min-height: 0;
      }

      /* Image panel fills its cell */
      .amenities-row-image {
        position: relative;
        overflow: hidden;
        height: 100%;
      }
      .amenities-row-image .amenities-slider {
        height: 100%;
      }

      /* Tablet: keep side-by-side layout, just slightly shorter row. */
      @media (max-width: 900px) {
        .amenities-row {
          height: 420px;
        }
      }

      /* Mobile: stack image on top, content below — but give the content
         area a fixed height so pagination still has room to detect
         overflow and slice the list into pages. */
      @media (max-width: 768px) {
        .amenities-row {
          grid-template-columns: 1fr;
          grid-template-rows: 220px 360px;
          height: auto;
        }
        .amenities-row--no-image {
          grid-template-rows: auto;
          min-height: 220px;
        }
        /* Image on top regardless of the desktop stagger. */
        .amenities-row--img-right > .amenities-row-content {
          order: 2;
        }
        .amenities-row--img-right > .amenities-row-image {
          order: 1;
        }
        .amenities-row-image {
          height: 220px;
        }
        .amenities-row-image .amenities-slider {
          height: 220px;
        }
      }

      @media (max-width: 600px) {
        .amenities-row {
          grid-template-rows: 200px 340px;
        }
        .amenities-row-image,
        .amenities-row-image .amenities-slider {
          height: 200px;
        }
      }

      /* Photo slider */
      .amenities-slider {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        flex-shrink: 0;
      }
      .amenities-slider-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        opacity: 0;
        transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .amenities-slider-img--active {
        opacity: 1;
      }

      .amenities-slider-arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: rgba(0, 0, 0, 0.35);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.3s, background 0.3s;
        z-index: 2;
      }
      .amenities-slider:hover .amenities-slider-arrow {
        opacity: 1;
      }
      .amenities-slider-arrow:hover {
        background: rgba(0, 0, 0, 0.55);
      }
      .amenities-slider-arrow--prev { left: 10px; }
      .amenities-slider-arrow--next { right: 10px; }

      .amenities-slider-dots {
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 6px;
        z-index: 2;
      }
      .amenities-slider-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.7);
        background: transparent;
        padding: 0;
        cursor: pointer;
        transition: background 0.25s, border-color 0.25s, transform 0.25s;
      }
      .amenities-slider-dot:hover {
        transform: scale(1.25);
        border-color: white;
      }
      .amenities-slider-dot--active {
        background: white;
        border-color: white;
      }

      @media (prefers-reduced-motion: reduce) {
        .amenities-slider-img {
          transition: none;
        }
      }

      .amenities-col-heading {
        font-family: var(--vibe-font-heading);
        font-size: 2rem;
        font-weight: 700;
        color: var(--vibe-color-text);
        text-align: left;
        padding: 1.5rem 2rem 0.25rem;
        margin: 0;
        letter-spacing: 0.02em;
      }

      @media (max-width: 900px) {
        .amenities-col-heading {
          font-size: 1.5rem;
          padding: 1rem 1rem 0.25rem;
        }
      }
      @media (max-width: 600px) {
        .amenities-col-heading {
          font-size: 1.15rem;
          padding: 0.75rem 0.75rem 0.25rem;
          letter-spacing: 0.01em;
          text-align: center;
        }
      }

      /* List area takes the remaining vertical space inside the row */
      .amenities-list-area {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }
      .amenities-list-area-inner {
        flex: 1;
        position: relative;
        overflow: hidden;
        min-height: 0;
      }

      .amenities-col-list {
        list-style: none;
        padding: 0.75rem 2rem 0.75rem;
        margin: 0;
        /* Two-column grid keeps overflow detection simple. */
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.25rem 1.5rem;
        align-content: start;
      }
      /* Hidden measurement copy: full list, no height cap. */
      .amenities-col-list--measure {
        position: absolute;
        inset: 0;
        visibility: hidden;
        pointer-events: none;
        height: auto;
      }

      /* Page-change slide animations. The new page slides in from the side
         matching the navigation direction. The list area clips overflow, so
         the off-screen start offset is never visible. */
      @keyframes amenities-slide-in-next {
        from { opacity: 0; transform: translateX(28px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes amenities-slide-in-prev {
        from { opacity: 0; transform: translateX(-28px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      .amenities-col-list--in-next {
        animation: amenities-slide-in-next 0.35s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .amenities-col-list--in-prev {
        animation: amenities-slide-in-prev 0.35s cubic-bezier(0.22, 1, 0.36, 1);
      }
      @media (prefers-reduced-motion: reduce) {
        .amenities-col-list--in-next,
        .amenities-col-list--in-prev {
          animation: none;
        }
      }

      /* Tablet: keep two columns, tighten spacing. */
      @media (max-width: 900px) {
        .amenities-col-list {
          padding: 0.5rem 1rem 0.5rem;
          gap: 0.2rem 0.75rem;
        }
      }
      /* Mobile: keep two columns side-by-side, even tighter. */
      @media (max-width: 600px) {
        .amenities-col-list {
          padding: 0.4rem 0.75rem 0.4rem;
          gap: 0.15rem 0.5rem;
        }
      }

      .amenities-col-item {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.3rem 0;
        font-family: var(--vibe-font-body);
        font-size: 0.88rem;
        color: var(--vibe-color-text);
        line-height: 1.5;
      }

      @media (max-width: 900px) {
        .amenities-col-item {
          font-size: 0.8rem;
          padding: 0.2rem 0;
          gap: 0.4rem;
        }
      }
      @media (max-width: 600px) {
        .amenities-col-item {
          font-size: 0.72rem;
          padding: 0.15rem 0;
          gap: 0.3rem;
          line-height: 1.35;
        }
      }

      .amenities-col-bullet {
        flex-shrink: 0;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--vibe-color-primary);
      }

      /* Pagination controls — only rendered when list overflows */
      .amenities-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.6rem;
        padding: 0.25rem 2rem 1rem;
        font-family: var(--vibe-font-body);
        font-size: 0.85rem;
        color: var(--vibe-color-text);
      }
      @media (max-width: 900px) {
        .amenities-pagination {
          padding: 0.25rem 1rem 0.75rem;
          gap: 0.4rem;
          font-size: 0.78rem;
        }
      }
      @media (max-width: 600px) {
        .amenities-pagination {
          padding: 0.25rem 0.75rem 0.6rem;
          gap: 0.35rem;
          font-size: 0.72rem;
        }
        .amenities-pagination-btn {
          width: 24px;
          height: 24px;
        }
        .amenities-pagination-info {
          min-width: 2.5rem;
        }
      }
      .amenities-pagination-btn {
        width: 28px;
        height: 28px;
        padding: 0;
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.15);
        background: white;
        color: var(--vibe-color-text);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
      }
      .amenities-pagination-btn:hover:not(:disabled) {
        background: var(--vibe-color-primary);
        border-color: var(--vibe-color-primary);
        color: white;
      }
      .amenities-pagination-btn:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
      .amenities-pagination-info {
        min-width: 3rem;
        text-align: center;
        font-variant-numeric: tabular-nums;
      }
    `}</style>
  );
}
